import { useEffect, useState } from 'preact/hooks';
import { type CostSummary, DashboardApi, HttpError, type UsageSummary } from '../api.js';
import type { AuthOrg } from '../auth.js';
import { Banner } from '../components/Banner.js';

const api = new DashboardApi();

const PLAN_NAMES: Record<AuthOrg['plan'], string> = {
  free: 'Free',
  pro: 'Pro — $29/user/mo',
  platform: 'Platform — from $499/site/mo',
};

export function Billing({ org }: { org: AuthOrg }): preact.ComponentChildren {
  const [busy, setBusy] = useState<'pro' | 'platform' | 'portal' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [cost, setCost] = useState<CostSummary | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [u, c] = await Promise.all([
          api.getUsage(org.id).catch(() => null),
          api.getCost(org.id).catch(() => null),
        ]);
        setUsage(u);
        setCost(c);
      } catch {
        /* best-effort; missing DB on dev env is fine */
      }
    })();
  }, [org.id]);

  const upgrade = async (plan: 'pro' | 'platform') => {
    setBusy(plan);
    setError(null);
    try {
      const r = await api.startCheckout(org.id, plan);
      window.location.href = r.url;
    } catch (e) {
      const msg =
        e instanceof HttpError && e.code === 'billing_disabled'
          ? 'Stripe is not configured on this environment. Configure STRIPE_SECRET_KEY + STRIPE_WEBHOOK_SECRET on the backend.'
          : e instanceof HttpError
            ? e.message
            : String(e);
      setError(msg);
      setBusy(null);
    }
  };

  const manage = async () => {
    setBusy('portal');
    setError(null);
    try {
      const r = await api.openPortal(org.id);
      window.location.href = r.url;
    } catch (e) {
      const msg =
        e instanceof HttpError && e.code === 'no_subscription'
          ? 'No subscription on file yet — upgrade to a paid plan first.'
          : e instanceof HttpError
            ? e.message
            : String(e);
      setError(msg);
      setBusy(null);
    }
  };

  return (
    <>
      <div class="page-header">
        <h1>Billing</h1>
        <p>Manage your subscription, switch plans, or update your payment method.</p>
      </div>

      {error && <Banner variant="warn">{error}</Banner>}

      <div class="card">
        <h2>Current plan</h2>
        <p class="lead" style={{ margin: '8px 0 16px' }}>
          {PLAN_NAMES[org.plan]}
        </p>
        <button type="button" disabled={busy !== null} onClick={() => void manage()}>
          {busy === 'portal' ? 'Opening…' : 'Manage subscription'}
        </button>
      </div>

      {org.plan === 'free' && (
        <div class="card" style={{ borderColor: 'var(--accent, #4da3ff)' }}>
          <h2>Try Pro free for 14 days</h2>
          <p class="desc">
            No credit card required up front. Full Pro quota for 14 days; if you don't add a card,
            we automatically revert to Free at the end of the trial.
          </p>
          <button
            type="button"
            disabled={busy !== null}
            onClick={async () => {
              setBusy('pro');
              setError(null);
              try {
                const r = await api.startTrial(org.id, 'pro');
                window.location.href = r.url;
              } catch (e) {
                setError(
                  e instanceof HttpError && e.code === 'billing_disabled'
                    ? 'Stripe is not configured on this environment.'
                    : e instanceof HttpError
                      ? e.message
                      : String(e),
                );
                setBusy(null);
              }
            }}
          >
            {busy === 'pro' ? 'Starting trial…' : 'Start 14-day Pro trial'}
          </button>
        </div>
      )}

      {cost && (
        <div class="card">
          <h2>This month's cost ({cost.month})</h2>
          <p class="lead" style={{ margin: '8px 0' }}>
            <strong>${cost.totalCostUsd.toFixed(2)}</strong> in LLM compute.
            {cost.cacheSavedUsd > 0 && (
              <>
                {' '}
                <span style={{ color: 'var(--success, #4ade80)' }}>
                  ${cost.cacheSavedUsd.toFixed(2)} saved by cache
                </span>
                {cost.cacheHitRatePct !== null && ` (${cost.cacheHitRatePct}% hit rate)`}.
              </>
            )}
          </p>
          {cost.byCapability.length > 0 && (
            <table class="audit">
              <thead>
                <tr>
                  <th>Capability</th>
                  <th>Calls</th>
                  <th>Cache hits</th>
                  <th>Tokens (in / out)</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {cost.byCapability.map((c) => (
                  <tr key={c.capability}>
                    <td>
                      <code class="meta">{c.capability}</code>
                    </td>
                    <td>{c.invocations.toLocaleString()}</td>
                    <td>{c.cacheHits.toLocaleString()}</td>
                    <td>
                      {c.tokensIn.toLocaleString()} / {c.tokensOut.toLocaleString()}
                    </td>
                    <td>${c.costUsd.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p class="small muted" style={{ marginTop: 8 }}>
            Costs are estimated using a blended provider rate; final invoicing uses per-call
            exactness. Cache hits cost $0.
          </p>
        </div>
      )}

      {usage && (
        <div class="card">
          <h2>This month's usage ({usage.month})</h2>
          {usage.limit === null ? (
            <p class="lead">
              <strong>{usage.used.toLocaleString()}</strong> calls — unlimited on the {usage.plan}{' '}
              plan.
            </p>
          ) : (
            <>
              <p class="lead">
                <strong>{usage.used.toLocaleString()}</strong> of{' '}
                <strong>{usage.limit.toLocaleString()}</strong> calls used (
                {Math.round((usage.used / usage.limit) * 100)}%).
              </p>
              <div
                aria-label="Quota used"
                role="progressbar"
                tabIndex={0}
                aria-valuenow={usage.used}
                aria-valuemin={0}
                aria-valuemax={usage.limit}
                style={{
                  height: 8,
                  background: 'var(--bg-subtle, #1a1f29)',
                  borderRadius: 4,
                  overflow: 'hidden',
                  margin: '8px 0 16px',
                }}
              >
                <div
                  style={{
                    width: `${Math.min(100, (usage.used / usage.limit) * 100)}%`,
                    height: '100%',
                    background:
                      usage.used / usage.limit > 0.9 ? 'var(--danger)' : 'var(--accent, #4da3ff)',
                  }}
                />
              </div>
            </>
          )}
          {Object.keys(usage.byCapability).length > 0 && (
            <table class="audit">
              <thead>
                <tr>
                  <th>Capability</th>
                  <th>Calls</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(usage.byCapability)
                  .sort(([, a], [, b]) => b - a)
                  .map(([cap, n]) => (
                    <tr key={cap}>
                      <td>
                        <code class="meta">{cap}</code>
                      </td>
                      <td>{n.toLocaleString()}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {org.plan !== 'platform' && (
        <div class="card">
          <h2>Upgrade</h2>
          <p class="desc">
            Bigger plans get unlimited cloud capabilities, premium voices, and a court-defensible
            audit log.
          </p>
          <div class="plan-grid">
            {org.plan === 'free' && (
              <div class="plan">
                <h3>Pro</h3>
                <p class="plan-price">
                  <span class="amount">$29</span>
                  <span class="cadence">/user/mo</span>
                </p>
                <ul>
                  <li>Unlimited cloud capabilities</li>
                  <li>Premium TTS voices</li>
                  <li>Cross-site preference sync</li>
                </ul>
                <button type="button" disabled={busy !== null} onClick={() => void upgrade('pro')}>
                  {busy === 'pro' ? 'Redirecting…' : 'Upgrade to Pro'}
                </button>
              </div>
            )}
            <div class="plan plan-highlight">
              <h3>Platform</h3>
              <p class="plan-price">
                <span class="amount">from $499</span>
                <span class="cadence">/site/mo</span>
              </p>
              <ul>
                <li>Embed script for unlimited visitors</li>
                <li>Build-time cache (sub-50ms)</li>
                <li>VPAT + audit log + SLA</li>
                <li>SSO + RBAC</li>
              </ul>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => void upgrade('platform')}
              >
                {busy === 'platform' ? 'Redirecting…' : 'Upgrade to Platform'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div class="card">
        <h2>Need a custom plan?</h2>
        <p class="desc">
          Public-sector, high-volume, or self-hosted? We offer custom pricing with dedicated
          support, BAA / DPA, and EU data residency.
        </p>
        <a class="btn-link" href="mailto:enterprise@wholisphere.ai">
          enterprise@wholisphere.ai
        </a>
      </div>
    </>
  );
}
